import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defaultSaveOptions, SaveOptions } from './types';
import { LinkBroken, Paperclip, Trash2 } from '@lifeomic/chromicons';
import { AttachmentList } from '../Attachments/AttachmentList';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import {
  AttachmentArbiterProvider,
  useAttachmentArbiter,
} from '../AttachmentArbiter';
import { useActiveProjectId, useService } from '../../../../../hooks';
import { useMixpanel } from '../../../../../hooks/useMixpanel';
import {
  MarkdownEditor,
  useInsertAttachment,
  useSave,
  useStripCitations,
} from './MarkdownEditor';
import { DiffHunkBulkActions } from './MarkdownEditor/plugins/DiffHunkBulkActions';
import { KeyFindings } from '../../ExecutiveSummary/KeyFindings';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { theme } from '../../../../../theme/phc';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { defineMessages, useIntl } from 'react-intl';
import clsx from 'clsx';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { isAiGenerated } from '../../ExecutiveSummary/utils';
import { Pill } from '@lifeomic/chroma-react/components/Pill';
import { Alert, AlertBody } from '@lifeomic/chroma-react/components/Alert';
import EnforceABAC, { permissions } from '../../../../EnforceABAC';
import { ICON_MAP } from '../../../../AITemplates/ImpressionTypeFilter';
import { ViewMode } from '../Attachments/InlineAttachment';

import { AxiosError } from 'axios';
import moment from 'moment';
import { TemporaryAttachment } from '../AttachmentArbiter/Attachment';
import { useSharedStyles } from '../../ExecutiveSummary/styles';
import {
  getEditorCanvasBackground,
  PHC_DOCUMENT_SURFACE_CLASS,
  usesWhiteDocumentSurface,
  useDocumentSurfaceStyles,
} from '../../ExecutiveSummary/documentSurface';
import {
  IMPRESSION_AI_GENERATED,
  ImpressionItem,
  REVIEW_DIFF_DOCUMENT_SYSTEM,
  REVIEW_PROPOSED_CITATIONS_SYSTEM,
  SUGGESTION_RATIONALE_SYSTEM,
} from './ImpressionItem';
import { reconcileInvestigationOnApply } from './reconcileCitations';
import { FOUNTAIN_SUGGESTION_LANGFUSE_TRACE_ID_EXTENSION } from '../../../../../util/zoriEditingExtensions';
import {
  ImpressionEditorProvider,
  useImpressionEditorContext,
} from './Context';
import { useEditorKeyboardShortcuts } from './useKeyboardShortcuts';
import { useNotify } from '../../../../../hooks/useNotify';
import { ClinicalImpressionService } from '../../../../../services/ClinicalImpressionService';
import { SYSTEM_LAST_EDITED_BY } from '../../../../../util/fhir';
import { upsertReviewBaseFingerprintExtension } from '../../../../../util/contentFingerprint';
import { usePastedFile } from './usePastedFile';

const useStyles = makeStyles<{ isEditing: boolean; viewMode: ViewMode }>(
  (theme: Theme) => ({
    root: ({ viewMode, isEditing }) => ({
      backgroundColor: getEditorCanvasBackground(theme, viewMode),
      borderRadius:
        viewMode === 'presentation-expanded' ||
        viewMode === 'presentation-compact' ||
        viewMode === 'print'
          ? theme.pxToRem(10)
          : 0,
      boxShadow: ({ viewMode }) =>
        viewMode === 'print' ? undefined : theme.boxShadows.table,
      flexDirection: 'column',
      overflow: 'auto',
      padding: theme.spacing(3),
      // clear the save section when editing, otherwise default padding
      paddingBottom: isEditing ? theme.spacing(6) : theme.spacing(3),
      width: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
    }),
    titleTextField: {
      marginBottom: theme.spacing(1),
    },
    attachmentsContainer: ({ isEditing, viewMode }) => ({
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      ...(isEditing && {
        position: 'sticky',
        top: theme.spacing(-3),
        backgroundColor: getEditorCanvasBackground(theme, viewMode),
        zIndex: 5,
        marginLeft: theme.spacing(-1.5),
        marginRight: theme.spacing(-1.5),
        marginTop: theme.spacing(-1),
      }),
    }),
    attachments: {
      gap: theme.spacing(2),
      width: '100%',
      alignItems: 'center',
      minHeight: 'fit-content',
    },
    attachmentsBar: {
      width: '100%',
      alignItems: 'center',
      flexWrap: 'wrap',
      rowGap: theme.spacing(1),
    },
    attachmentsListCluster: {
      flex: 1,
      minWidth: 0,
      gap: theme.spacing(2),
      alignItems: 'center',
    },
    paperclip: ({ isEditing }) => ({
      color: theme.palette.text.secondary,
      minWidth: 22,
      marginLeft: theme.spacing(1.25),
      marginRight: theme.spacing(0.5),
      ...(isEditing && {
        marginLeft: theme.spacing(2.5),
      }),
    }),
    summary: ({ isEditing, viewMode }) => ({
      flex: 1,
      lineHeight: 1.6,
      color: theme.palette.text.primary,
      borderRadius: theme.pxToRem(4),
      padding: theme.spacing(
        viewMode === 'presentation-expanded' ||
          viewMode === 'presentation-compact' ||
          viewMode === 'print'
          ? 0
          : 1,
        isEditing ? 1 : 0,
      ),
      position: 'relative',
      backgroundColor:
        isEditing &&
        viewMode !== 'presentation-expanded' &&
        viewMode !== 'presentation-compact'
          ? theme.palette.mode === 'dark'
            ? theme.palette.action.hover
            : theme.hexToRgba(theme.palette.graphite[900], 0.15)
          : 'transparent',
      border: `1px solid transparent`,
      transition: 'border 0.25s ease',

      // when editing, margin bottom for save section
      marginBottom: theme.spacing(
        isEditing &&
          viewMode !== 'presentation-expanded' &&
          viewMode !== 'presentation-compact' &&
          viewMode !== 'print'
          ? 8
          : 0,
      ),

      '&:has(.public-DraftEditor-content:focus)': {
        backgroundColor: isEditing
          ? theme.palette.mode === 'dark'
            ? theme.palette.action.selected
            : theme.hexToRgba(theme.palette.common.white, 0.5)
          : theme.palette.mode === 'dark'
          ? theme.palette.action.hover
          : theme.hexToRgba(theme.palette.graphite[900], 0.15),
        borderColor: isEditing
          ? theme.palette.mode === 'dark'
            ? theme.palette.divider
            : theme.hexToRgba(theme.palette.graphite[900], 0.45)
          : undefined,
      },

      '& table': {
        borderCollapse: 'collapse',
        width: '100%',
        '& + p': {
          marginTop: theme.spacing(5),
        },
      },
      '& thead': {
        display: 'none',
      },
      '& th, & td': {
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.secondary,
        fontSize: theme.typography.body2.fontSize,
        lineHeight: 1.5,
        minWidth: theme.pxToRem(110),
        padding: theme.spacing(1, 1.5),
        '&:first-child': {
          background: theme.hexToRgba(theme.palette.graphite[900], 0.05),
          color: theme.palette.text.hint,
          fontWeight: theme.typography.fontWeightBold,
        },
      },
      '& > div:has($buttonControl)': {
        backgroundColor: theme.hexToRgba(theme.palette.graphite[900], 0.15),
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.pxToRem(4)} ${theme.pxToRem(4)} 0 0`,
        padding: theme.spacing(0.75),
        position: 'relative',
        top: theme.spacing(-1),
        left: theme.spacing(-1),
        width: `calc(100% + ${theme.spacing(2)})`,
        zIndex: 1,
      },
      '& div:has($buttonControl)': {
        marginBottom: 0,
      },
      '& > div:first-child': {
        paddingVertical: isEditing ? theme.spacing(1) : 0,
        paddingTop: theme.spacing(1),
        zIndex: 2,
        marginLeft: theme.spacing(-1),
        borderRadius: theme.pxToRem(4),
        marginRight: theme.spacing(-1),
        marginTop: theme.spacing(-1),
        backgroundColor: getEditorCanvasBackground(theme, viewMode),
        position: 'sticky',
        top: 42,
      },
      '& h1': {
        fontSize: theme.typography.h3.fontSize,
        color: theme.palette.text.primary,
      },
      '& h2': {
        fontSize: theme.typography.h4.fontSize,
        color: theme.palette.text.primary,
      },
      '& h3': {
        fontSize: theme.typography.h5.fontSize,
        color: theme.palette.text.primary,
      },
      '& h4, & h5, & h6': {
        fontSize: theme.typography.h6.fontSize,
        color: theme.palette.text.primary,
      },
      '& [contenteditable="true"]': {
        color: theme.palette.text.primary,
        '& h1, & h2, & h3, & h4, & h5, & h6, & p, & li': {
          color: theme.palette.text.primary,
        },
      },
    }),
    saveSection: ({ viewMode }) => ({
      borderTop: `1px solid ${theme.palette.divider}`,
      position: 'absolute',
      left: theme.spacing(2),
      bottom: theme.spacing(2),
      backgroundColor: getEditorCanvasBackground(theme, viewMode),
      padding: theme.spacing(2),
      width: `calc(100% - ${theme.spacing(4)})`,
      zIndex: 2,
      animation: '$fadeIn 0.25s ease-out 0.5s backwards',
    }),
    alert: {
      marginBottom: theme.spacing(1),
    },
    alertBody: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(2.5),
    },
    aiSuggestedPill: {
      color: theme.palette.common.white,
      height: theme.pxToRem(25),
      padding: theme.spacing(1),
      flexShrink: 0,
    },
    buttonControl: {},
  }),
);

const messages = defineMessages({
  delete: {
    id: 'impressionEditor.delete',
    defaultMessage: 'Delete',
  },
  save: {
    id: 'impressionEditor.save',
    defaultMessage: 'Save',
  },
  discard: {
    id: 'impressionEditor.discard',
    defaultMessage: 'Discard',
  },
  changesSaved: {
    id: 'impressionEditor.changesSaved',
    defaultMessage: 'Changes saved',
  },
  saveConflict: {
    id: 'impressionEditor.saveConflict',
    defaultMessage:
      'This report was updated elsewhere. Loading the latest version from the server—review the text, then save again.',
  },
  saveBlockedBySuggesting: {
    id: 'impressionEditor.saveBlockedBySuggesting',
    defaultMessage:
      'Zori is still reviewing your feedback. Saving will be available once requested edits arrive.',
  },
  title: {
    id: 'impressionEditor.title',
    defaultMessage: 'Title',
  },
  aiGenerated: {
    id: 'impressionEditor.aiGenerated',
    defaultMessage: 'AI Generated',
  },
  lastEditedBy: {
    id: 'impressionEditor.lastEditedBy',
    defaultMessage: 'Last edited by {name} on {time}',
  },
  aiSuggestedAlert: {
    id: 'impressionEditor.actionItemModal.aiSuggestedAlert',
    defaultMessage:
      'This system summary was generated by Zori AI. Saving any changes will remove this label.',
  },
  uploading: {
    id: 'impressionEditor.uploading',
    defaultMessage: '[Uploading {name}...]',
  },
  removeAllAttachments: {
    id: 'impressionEditor.removeAllAttachments',
    defaultMessage: 'Remove All Attachments',
  },
});

type ImpressionEditorProps = {
  className?: string;
  /**
   * Only needed when editing. A key for the `ClinicalImpression` extension
   * that contains the ID of the user who last edited the impression.
   */
  lastEditedBySystem?: string;
  onDelete?: () => void;
  onSaveError?: (error: unknown) => void;
  onSave?: (updatedImpression: fhir.ClinicalImpression) => void;
  hideTitle?: boolean;
  /**
   * Called after a pasted file is successfully uploaded.
   */
  onFileUpload?: () => void;
};

const ImpressionEditorRender = (props: ImpressionEditorProps) => {
  const {
    onDelete,
    onSave,
    onSaveError,
    onFileUpload,
    hideTitle,
    lastEditedBySystem = SYSTEM_LAST_EDITED_BY,
  } = props;

  const mixpanel = useMixpanel();
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const {
    refresh,
    refetchImpression,
    activeUserId,
    isEditing,
    setIsEditing,
    viewMode,
    patient,
    activeImpression: impression,
    isSuggesting,
  } = useImpressionEditorContext();
  const [saveImpression] = useService(
    new ClinicalImpressionService().saveImpression,
    { rethrowErrors: true },
  );
  const projectId = useActiveProjectId();
  const { handlePastedFile: _handlePastedFile } = usePastedFile(onFileUpload);
  const [isSaving, setIsSaving] = useState(false);
  const [title, setTitle] = useState<string>(impression.title ?? '');
  const [findings, setFindings] = useState<string[]>(impression.findings ?? []);

  // Tracks the summary that was last successfully persisted to the server.
  // Used as the fingerprint base in saveImpl instead of impression.impression.summary,
  // which can be mutated in place (e.g. by applyStrippedSummaryMarkdown) before the
  // save fires, causing a stale SHA-256 and a 409 from patient-service.
  //
  // Keyed on object identity: when the parent supplies a fresh ImpressionItem after a
  // server refresh (e.g. after a 409 conflict or after refetchImpression resolves), the
  // ref resets to the new server summary. In-place mutations of the same object (e.g.
  // applyStrippedSummaryMarkdown) do not change the reference, so the ref is correctly
  // unaffected by those mutations.
  //
  // CONTRACT: callers that refetch and update this impression (refresh / refetchImpression)
  // must supply a NEW ImpressionItem reference rather than mutating the existing one.
  // ReportEditor.tsx satisfies this via `setImpressionOverride(new ImpressionItem(updated))`.
  const lastPersistedSummaryRef = useRef<string>(impression.summary ?? '');
  useEffect(() => {
    lastPersistedSummaryRef.current = impression.summary ?? '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impression]); // object identity — fires on fresh ImpressionItem, not on in-place mutations

  const onFindingsChange = useCallback(
    (newFindings: string[]) => {
      if (
        newFindings.length !== findings.length ||
        !newFindings.every((f, i) => f === findings[i])
      ) {
        setFindings(newFindings);
      }
    },
    [findings],
  );

  const saveImpl = useCallback(
    async (
      markdown: string,
      {
        andExitEdit,
        andNotify,
        clearReviewDiff,
      }: SaveOptions = defaultSaveOptions,
    ) => {
      if (impression.reviewDiffDocument && !clearReviewDiff) {
        console.warn(
          '[ImpressionEditor] Save suppressed — reviewDiffDocument is active. Resolve all diffs first.',
        );
        return;
      }
      if (isSuggesting && !clearReviewDiff) {
        // A suggest-agent invocation is in flight; saving now would
        // push the user's stale pre-suggestion content over whatever
        // Zori produces, silently discarding the suggestions.
        console.warn(
          '[ImpressionEditor] Save suppressed — a suggest invocation is in progress.',
        );
        return;
      }
      setIsSaving(true);
      let saveSucceeded = false;
      const previousTitle = impression.impression.description;
      const previousSummary = impression.impression.summary;
      const updatedSummary = markdown;
      const previousFindings = impression.impression.finding;
      const previousExtension = impression.impression.extension;
      const previousInvestigation = impression.impression.investigation
        ? (JSON.parse(
            JSON.stringify(impression.impression.investigation),
          ) as typeof impression.impression.investigation)
        : undefined;

      try {
        impression.impression.description = title;
        impression.impression.summary = updatedSummary;
        const updatedFindings = findings.map(finding => ({
          itemReference: { display: finding },
        }));
        impression.impression.finding = updatedFindings;
        const updatedInvestigation = reconcileInvestigationOnApply(
          impression,
          updatedSummary,
        );
        impression.impression.investigation = updatedInvestigation;

        const baseSummaryForFingerprint = lastPersistedSummaryRef.current;
        let extensions = await upsertReviewBaseFingerprintExtension(
          impression.impression.extension,
          baseSummaryForFingerprint,
        );
        if (clearReviewDiff) {
          extensions = extensions.filter(
            ext =>
              ext.url !== REVIEW_DIFF_DOCUMENT_SYSTEM &&
              ext.url !== REVIEW_PROPOSED_CITATIONS_SYSTEM &&
              ext.url !== SUGGESTION_RATIONALE_SYSTEM &&
              ext.url !== FOUNTAIN_SUGGESTION_LANGFUSE_TRACE_ID_EXTENSION,
          );
        }
        impression.impression.extension = extensions;
        const lastEditedExt = extensions.find(
          ext => ext.url === lastEditedBySystem,
        );
        if (lastEditedExt) {
          lastEditedExt.valueString = activeUserId;
        } else {
          extensions.push({
            url: lastEditedBySystem,
            valueString: activeUserId,
          });
        }

        const aiGeneratedExt = extensions.find(
          ext => ext.url === IMPRESSION_AI_GENERATED,
        );
        if (aiGeneratedExt) {
          aiGeneratedExt.valueBoolean = false;
        }

        const { data: updatedImpression } = await saveImpression({
          patientId: patient.id,
          projectId: projectId,
          impressionId: impression.id,
          impression: {
            ...impression.impression,
            description: title,
            summary: updatedSummary,
            finding: updatedFindings,
            extension: extensions,
            investigation: updatedInvestigation,
          },
        });
        lastPersistedSummaryRef.current = updatedSummary;
        if (andExitEdit) {
          setIsEditing(false);
        }
        if (andNotify) {
          notify(formatMessage(messages.changesSaved), 'success', 5000);
        }
        onSave?.(updatedImpression);
        saveSucceeded = true;
      } catch (err) {
        impression.impression.summary = previousSummary;
        impression.impression.finding = previousFindings;
        impression.impression.description = previousTitle;
        impression.impression.extension = previousExtension;
        impression.impression.investigation = previousInvestigation;
        onSaveError?.(err);
        const isConflict =
          err instanceof AxiosError && err.response?.status === 409;
        let errorText: string;
        if (isConflict) {
          errorText = formatMessage(messages.saveConflict);
        } else if (err instanceof Error) {
          errorText = err.message;
        } else {
          errorText = 'Could not save changes.';
        }
        notify(errorText, 'error');
        if (isConflict) {
          refresh();
        }
      } finally {
        setIsSaving(false);
      }
      if (saveSucceeded && clearReviewDiff) {
        try {
          await refetchImpression?.(impression.id);
        } catch (refetchErr) {
          // Refetch failure is non-fatal — the save committed server-side.
          // The drawer will stay locked until the next interaction that
          // triggers a fresh ImpressionItem (e.g. navigating away and back).
          console.warn(
            '[ImpressionEditor] refetchImpression failed after clearReviewDiff save',
            { impressionId: impression.id, error: refetchErr },
          );
        }
      }
    },
    [
      impression,
      title,
      findings,
      lastEditedBySystem,
      activeUserId,
      saveImpression,
      patient.id,
      projectId,
      onSave,
      setIsEditing,
      notify,
      formatMessage,
      onSaveError,
      refresh,
      isSuggesting,
      refetchImpression,
    ],
  );

  const handleSave = useSave(saveImpl);

  useEditorKeyboardShortcuts(handleSave);
  const sharedClasses = useSharedStyles({ viewMode });
  const documentSurfaceClasses = useDocumentSurfaceStyles();
  const classes = useStyles({
    isEditing:
      isEditing ||
      viewMode === 'presentation-expanded' ||
      viewMode === 'presentation-compact',
    viewMode,
  });
  const { addAttachment } = useAttachmentArbiter();

  const rootRef = useRef<HTMLDivElement>(null);
  const trackCopyEvent = useCallback(
    (target: string) => () => {
      mixpanel.track('annual_report_text_copy', {
        target,
      });
    },
    [mixpanel],
  );

  const trackLoadSummaryEvent = useCallback(
    (impressionType: string) => {
      mixpanel.track('annual_report_load_summary', {
        target: 'page',
        impressionType,
      });
    },
    [mixpanel],
  );

  useEffect(() => {
    const handleCopy = trackCopyEvent('page');

    window.addEventListener('copy', handleCopy);

    return () => window.removeEventListener('copy', handleCopy);
  }, [trackCopyEvent]);

  const handleCopy = (target: string) => {
    return (e: React.ClipboardEvent) => {
      e.stopPropagation();
      trackCopyEvent(target)();
    };
  };

  const Icon = ICON_MAP[impression.impressionType];

  useEffect(() => {
    trackLoadSummaryEvent(impression.impressionType ?? '');

    if (!isEditing) {
      rootRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

      // second scroll to top to try to work around scroll issue on slower machines
      // to work around slow loading of markdown. consider removing if not useful.
      setTimeout(() => {
        rootRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }, 750);
    }
  }, [
    impression.summary,
    viewMode,
    isEditing,
    impression.impressionType,
    trackLoadSummaryEvent,
  ]);

  const onUseAttachment = useInsertAttachment();
  const stripCitationsFromEditor = useStripCitations();

  const handlePastedFile = (files: File[]) => {
    const file = files[0];
    if (!file) return;

    _handlePastedFile?.(file).then(reference => {
      if (!reference) return;
      if (reference.reference) {
        addAttachment(
          reference.reference,
          new TemporaryAttachment(
            reference,
            `${new Date().toISOString()}-${file.name}`,
          ),
        );
      }
      onUseAttachment(reference, impression.attachments.length);
    });
  };

  const onDiscard = (
    { andExitEdit }: Omit<SaveOptions, 'andNotify'> = {
      andExitEdit: true,
    },
  ) => {
    setFindings(impression.findings ?? []);
    setTitle(impression.title || '');
    if (andExitEdit) {
      setIsEditing(false);
    }
  };

  return (
    <>
      {(viewMode === 'presentation-expanded' ||
        viewMode === 'presentation-compact' ||
        viewMode === 'print') &&
        !hideTitle && (
          <Box className={sharedClasses.presentationHeader}>
            <Box direction="column" style={{ flex: 1 }}>
              <Box align="center" gap={2}>
                {Icon && (
                  <Box className={sharedClasses.impressionIconRoot}>
                    <Icon />
                  </Box>
                )}
                <Box direction="column">
                  <Text
                    size="headline"
                    className={sharedClasses.title}
                    onCopy={handleCopy('title')}
                    data-testid="es-details-title"
                  >
                    {impression.title}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      <Box
        className={clsx(
          classes.root,
          usesWhiteDocumentSurface(viewMode) && documentSurfaceClasses.root,
          usesWhiteDocumentSurface(viewMode) && PHC_DOCUMENT_SURFACE_CLASS,
          'es-details',
        )}
        onCopy={handleCopy('root')}
        data-testid="es-details"
        gap={1}
        ref={rootRef}
      >
        {viewMode !== 'presentation-expanded' &&
          viewMode !== 'presentation-compact' &&
          viewMode !== 'print' &&
          isAiGenerated(impression.impression) && (
            <Alert className={clsx(classes.alert)}>
              <AlertBody className={classes.alertBody}>
                <Pill
                  variant="highlight"
                  className={classes.aiSuggestedPill}
                  label={formatMessage(messages.aiGenerated)}
                />
                <Text size="subbody">
                  {formatMessage(messages.aiSuggestedAlert)}
                </Text>
              </AlertBody>
            </Alert>
          )}
        {isEditing ? (
          <TextField
            fullWidth
            className={clsx(classes.titleTextField)}
            label={formatMessage(messages.title)}
            value={title}
            onChange={val => setTitle(val.target.value)}
          />
        ) : (
          <>
            {viewMode !== 'presentation-expanded' &&
              viewMode !== 'presentation-compact' &&
              viewMode !== 'print' &&
              impression.lastEditedBy &&
              !hideTitle && (
                <Box
                  direction="column"
                  style={{ flex: 1, breakInside: 'avoid' }}
                >
                  <Box gap={1.5}>
                    {Icon && (
                      <Box className={sharedClasses.impressionIconRoot}>
                        <Icon />
                      </Box>
                    )}
                    <Box direction="column">
                      <Text
                        size="headline"
                        className={sharedClasses.title}
                        onCopy={handleCopy('title')}
                        data-testid="es-details-title"
                      >
                        {impression.title}
                      </Text>
                      <Box marginBottom={0.5}>
                        <Text size="caption">
                          {formatMessage(messages.lastEditedBy, {
                            name: impression.lastEditedBy,
                            time: moment(
                              impression.impression.meta?.lastUpdated,
                            ).format('MMM DD @ hh:mm a'),
                          })}
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
          </>
        )}

        <Box direction="column" style={{ flex: 1 }} gap={1}>
          {viewMode !== 'presentation-expanded' &&
            viewMode !== 'presentation-compact' &&
            viewMode !== 'print' &&
            (isEditing || impression.attachments.length > 0) && (
              <Box
                gap={1}
                direction="column"
                className={classes.attachmentsContainer}
              >
                <Box
                  direction="row"
                  className={clsx(classes.attachments, classes.attachmentsBar)}
                >
                  <Box
                    direction="row"
                    className={classes.attachmentsListCluster}
                    onCopy={handleCopy('attachments')}
                  >
                    <Paperclip className={classes.paperclip} />
                    <AttachmentList
                      attachments={impression.attachments}
                      onUseAttachment={onUseAttachment}
                      action={isEditing ? 'add' : 'readonly'}
                      patient={patient}
                      activeImpression={impression}
                      fetchCarePlan={refresh}
                    />
                  </Box>
                  {isEditing && (
                    <EnforceABAC permission={permissions.updateData}>
                      <Button
                        disabled={isSaving || isSuggesting}
                        icon={LinkBroken}
                        variant="text"
                        onClick={() => {
                          stripCitationsFromEditor();
                          mixpanel.track('zori_report_citations_stripped', {
                            impressionType: impression.impressionType,
                            impressionId: impression.id,
                          });
                        }}
                      >
                        {formatMessage(messages.removeAllAttachments)}
                      </Button>
                    </EnforceABAC>
                  )}
                </Box>
              </Box>
            )}

          <Box gap={1}>
            {impression.findings.length > 0 &&
              viewMode !== 'presentation-expanded' &&
              viewMode !== 'presentation-compact' &&
              viewMode !== 'print' && (
                <>
                  <Box
                    onCopy={handleCopy('findings')}
                    data-testid="es-details-findings"
                  >
                    <KeyFindings
                      isEditing={!!isEditing}
                      isDisabled={!!isSaving}
                      editingState={findings}
                      savedState={impression.findings}
                      onFindingsChange={onFindingsChange}
                    />
                  </Box>
                  <Box marginTop={0.5}>
                    <Divider className={classes.divider} />
                  </Box>
                </>
              )}
          </Box>

          <Box
            onCopy={handleCopy('summary')}
            className="es-details-summary"
            data-testid="es-details-summary"
          >
            <MarkdownEditor.Component
              onPastedFiles={handlePastedFile}
              onAllDiffsResolved={markdown =>
                saveImpl(markdown, {
                  andExitEdit: false,
                  andNotify: true,
                  clearReviewDiff: true,
                })
              }
            />
          </Box>
        </Box>
        {isEditing && (
          <Box className={classes.saveSection} gap={1}>
            <Box direction="column" style={{ flex: 1 }}>
              <Box
                justify="space-between"
                gap={theme.spacing(1)}
                align="center"
              >
                {impression.reviewDiffDocument ? (
                  <DiffHunkBulkActions />
                ) : (
                  <>
                    <Box justify="center" gap={theme.spacing(1)}>
                      <EnforceABAC
                        key="delete"
                        permission={permissions.deleteData}
                      >
                        <Button
                          color="negative"
                          disabled={isSaving}
                          icon={Trash2}
                          variant="text"
                          onClick={() => onDelete?.()}
                        >
                          {formatMessage(messages.delete)}
                        </Button>
                      </EnforceABAC>
                    </Box>
                    <Box justify="center" gap={theme.spacing(1)}>
                      {/* display prop instead of un rendering helps improve render speed */}
                      <DotLoader
                        size={0}
                        display={isSaving ? 'inherit' : 'none'}
                      />
                      <Button
                        disabled={isSaving || isSuggesting}
                        variant="text"
                        onClick={() => onDiscard({ andExitEdit: true })}
                      >
                        {formatMessage(messages.discard)}
                      </Button>
                      <Tooltip
                        title={
                          isSuggesting
                            ? formatMessage(messages.saveBlockedBySuggesting)
                            : formatMessage(messages.save)
                        }
                      >
                        <Box>
                          <Button
                            disabled={isSaving || isSuggesting}
                            variant="contained"
                            onClick={() =>
                              handleSave({
                                andExitEdit: true,
                                andNotify: true,
                              })
                            }
                          >
                            {formatMessage(messages.save)}
                          </Button>
                        </Box>
                      </Tooltip>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

type ImpressionEditorProvidersProps = {
  children: React.ReactNode;
  patient: fhir.Patient & { id: string };
  impression: ImpressionItem;
  viewMode: ViewMode;
  refresh?: () => void;
  refetchImpression?: (impressionId: string) => Promise<void>;
  isEditing?: boolean;
  setIsEditing?: (isEditing: boolean) => void;
  canAnnotate?: boolean;
  renderKnowledgeBaseCitations?: boolean;
};

const ImpressionEditorProviders = ({
  children,
  patient,
  impression,
  viewMode,
  refresh,
  refetchImpression,
  isEditing,
  setIsEditing,
  canAnnotate,
  renderKnowledgeBaseCitations = true,
}: ImpressionEditorProvidersProps) => {
  const attachments = useMemo(
    () =>
      impression.attachments
        .filter(
          (attachment): attachment is { reference: string } =>
            !!attachment.reference,
        )
        .map(attachment => attachment.reference),
    [impression],
  );
  return (
    <AttachmentArbiterProvider
      patient={patient}
      attachments={attachments}
      key={impression.id}
    >
      <ImpressionEditorProvider
        patient={patient}
        isEditing={!!isEditing}
        setIsEditing={setIsEditing ?? (() => void 0)}
        activeImpression={impression}
        viewMode={viewMode}
        refresh={refresh ?? (() => void 0)}
        refetchImpression={refetchImpression}
        canAnnotate={canAnnotate}
        renderKnowledgeBaseCitations={renderKnowledgeBaseCitations}
        key={impression.id}
      >
        <MarkdownEditor.Provider
          onError={console.error}
          namespace={`ClinicalImpression/${impression.id}`}
          key={`${impression.id}-${viewMode}`}
        >
          {children}
        </MarkdownEditor.Provider>
      </ImpressionEditorProvider>
    </AttachmentArbiterProvider>
  );
};

const ImpressionEditorComponent = (
  props: Omit<ImpressionEditorProvidersProps, 'children'> &
    ImpressionEditorProps,
) => {
  return (
    <ImpressionEditorProviders {...props}>
      <ImpressionEditorRender {...props} />
    </ImpressionEditorProviders>
  );
};

export const ImpressionEditor = {
  /**
   * A combination `ClinicalImpression` editor and viewer.
   */
  Component: ImpressionEditorComponent,
  /**
   * Provides all context required for the impression editor
   * and related components.
   */
  Providers: ImpressionEditorProviders,
  /**
   * The actual component that renders the impression viewer/editor.
   * Requires `ImpressionEditor.Providers` to be a parent.
   */
  Render: ImpressionEditorRender,
};
